import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import api from "@/lib/api";

export default function FiltersSidebar({ filters, onChange, onClear }) {
  const [localities, setLocalities] = useState({ pcmc: [], pune: [] });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/meta/localities").then((r) => setLocalities({ pcmc: r.data.pcmc || [], pune: r.data.pune || [] })).catch(() => {});
    api.get("/meta/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const set = (k, v) => onChange({ ...filters, [k]: v });

  const selectCls = "w-full h-10 rounded-xl border border-[rgba(17,24,39,0.14)] bg-white px-3 pr-9 text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#C9A86A]";

  return (
    <div className="space-y-6" data-testid="filters-sidebar">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg">Filters</h3>
        <Button data-testid="filters-clear-button" variant="ghost" size="sm" onClick={onClear} className="text-xs">
          Clear all
        </Button>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-[#4B5563]">Locality</Label>
        <div className="relative mt-1.5">
          <select
            data-testid="filter-locality-select"
            value={filters.locality || ""}
            onChange={(e) => set("locality", e.target.value)}
            className={selectCls}
          >
            <option value="">All localities</option>
            {localities.pcmc.length > 0 && (
              <optgroup label="PCMC">
                {localities.pcmc.map((l) => <option key={l} value={l}>{l}</option>)}
              </optgroup>
            )}
            {localities.pune.length > 0 && (
              <optgroup label="Pune">
                {localities.pune.map((l) => <option key={l} value={l}>{l}</option>)}
              </optgroup>
            )}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] pointer-events-none" />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-[#4B5563]">Category</Label>
        <div className="relative mt-1.5">
          <select
            data-testid="filter-category-select"
            value={filters.category || ""}
            onChange={(e) => set("category", e.target.value)}
            className={selectCls}
          >
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] pointer-events-none" />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-[#4B5563]">Minimum Rating</Label>
        <RadioGroup value={String(filters.min_rating || "0")} onValueChange={(v) => set("min_rating", v === "0" ? "" : parseFloat(v))} className="mt-2 space-y-2">
          {["0", "4", "4.5"].map((r) => (
            <div className="flex items-center gap-2" key={r}>
              <RadioGroupItem id={`rating-${r}`} value={r} data-testid={`filter-rating-${r}`} />
              <Label htmlFor={`rating-${r}`} className="text-sm cursor-pointer">{r === "0" ? "Any" : `${r}+`}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-[#4B5563]">Price Range</Label>
        <RadioGroup value={filters.price || "any"} onValueChange={(v) => set("price", v === "any" ? "" : v)} className="mt-2 space-y-2">
          {[{ v: "any", l: "Any" }, { v: "₹", l: "₹ Budget" }, { v: "₹₹", l: "₹₹ Mid" }, { v: "₹₹₹", l: "₹₹₹ Premium" }].map((p) => (
            <div key={p.v} className="flex items-center gap-2">
              <RadioGroupItem id={`price-${p.v}`} value={p.v} data-testid={`filter-price-${p.v}`} />
              <Label htmlFor={`price-${p.v}`} className="text-sm cursor-pointer">{p.l}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-3">
        <Label className="text-xs uppercase tracking-wide text-[#4B5563]">Highlights</Label>
        <div className="flex items-center gap-2">
          <Checkbox id="f-featured" checked={!!filters.featured} onCheckedChange={(v) => set("featured", !!v)} data-testid="filter-featured-checkbox" />
          <Label htmlFor="f-featured" className="text-sm cursor-pointer">Featured only</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="f-verified" checked={!!filters.verified} onCheckedChange={(v) => set("verified", !!v)} data-testid="filter-verified-checkbox" />
          <Label htmlFor="f-verified" className="text-sm cursor-pointer">Verified only</Label>
        </div>
      </div>
    </div>
  );
}
